import React, { useState, useEffect } from "react";
import { Select, message, Spin, Switch } from "antd";
import { ArrowLeft, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import DeleteIcon from "../../../../../Images/deleteIcon.png";
import "./AddScenario.css";

import {
  createScenario,
  updateScenario,
  getScenarioById,
  createScenarioQuestion,
  updateScenarioQuestion,
  deleteScenarioQuestion,
  getScenarioConfigurationOptions,
} from "../../../../../services/SyncApis";


const makeEmptyQuiz = () => ({
  id: null,
  quiz: "",
  description: "",
  questionTypeId: "",
  options: [
    {
      _id: null,
      text: "",
      isCorrect: false,
    },
    {
      _id: null,
      text: "",
      isCorrect: false,
    },
    {
      _id: null,
      text: "",
      isCorrect: false,
    },
    {
      _id: null,
      text: "",
      isCorrect: false,
    },
  ],
  weightage: "",
  isActive: true,
});


const getTextValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    if (typeof value.en === "string") {
      return value.en;
    }

    const firstString = Object.values(value).find(
      (item) => typeof item === "string"
    );

    return firstString || "";
  }

  return String(value);
};


const AddScenario = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const fullState = location.state || {};

  const roleId = fullState.roleId;
  const accountId = fullState.accountId;
  const changedBy = fullState.changedBy;

  const scenarioId = fullState.scenarioId;
  const editMode = fullState.editMode || false;


  /* =========================================================
     SCENARIO FORM
     ========================================================= */

  const [formData, setFormData] = useState({
    scenarioTypeId: "",
    videoCode: "",
    videoTitle: "",
    videoThumbnailCode: "",
    videoLanguageId: "",
    videoDescription: "",
    videoDuration: "",
  });


  /* =========================================================
     OPTIONS FROM BACKEND
     ========================================================= */

  const [scenarioTypes, setScenarioTypes] = useState([]);
  const [videoLanguages, setVideoLanguages] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [videoDuration, setVideoDuration] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);


  /* =========================================================
     QUIZZES
     ========================================================= */

  const [quizzes, setQuizzes] = useState([
    makeEmptyQuiz(),
  ]);
  const [expandedQuizzes, setExpandedQuizzes] = useState({
    0: true,
  });

  /* =========================================================
     ACTIVE / INACTIVE
     Only relevant in EDIT mode.
     ========================================================= */

  const [isScenarioActive, setIsScenarioActive] =
    useState(true);


  /* =========================================================
     UI STATE
     ========================================================= */

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);


  /* =========================================================
     LOAD SCENARIO OPTIONS
     ========================================================= */

  useEffect(() => {
    const loadOptions = async () => {
      setLoadingOptions(true);

      try {
        const response =
          await getScenarioConfigurationOptions(
            Number(roleId),
            {
              adminClientId: Number(accountId),
            }
          );

        const data =
          response?.data || response || {};

        setScenarioTypes(
          Array.isArray(data.scenarioTypes)
            ? data.scenarioTypes
            : []
        );

        setVideoLanguages(
          Array.isArray(data.videoLanguages)
            ? data.videoLanguages
            : []
        );

        setQuestionTypes(
          Array.isArray(data.questionTypes)
            ? data.questionTypes
            : []
        );
      } catch (error) {
        console.error(
          "Failed to load scenario options:",
          error
        );

        const backendMessage =
          error?.response?.data?.message;

        message.error(
          backendMessage ||
          "Failed to load scenario configuration options."
        );
      } finally {
        setLoadingOptions(false);
      }
    };

    loadOptions();
  }, [roleId, accountId]);


  /* =========================================================
     QUESTION TYPE HELPERS
     ========================================================= */

  const getQuestionTypeById = (id) => {
    return questionTypes.find(
      (type) =>
        Number(type.id) === Number(id)
    );
  };


  const getQuestionTypeValue = (id) => {
    return questionTypes.find(
      (item) =>
        Number(item.id) === Number(id)
    )?.value;
  };


  const isYesNoType = (id) => {
    return (
      getQuestionTypeValue(id) ===
      "YES/NO"
    );
  };


  const isMultiSelectType = (id) => {
    return (
      getQuestionTypeValue(id) ===
      "MULTI_SELECT"
    );
  };


  /* =========================================================
     OPTIONS FOR QUESTION TYPE
     ========================================================= */

  const createOptionsForType = (
    questionTypeId,
    existingOptions = []
  ) => {
    if (isYesNoType(questionTypeId)) {
      return [
        {
          _id:
            existingOptions?.[0]?._id ||
            null,
          text:
            existingOptions?.[0]?.text ||
            "True",
          isCorrect:
            existingOptions?.[0]?.isCorrect ||
            false,
        },
        {
          _id:
            existingOptions?.[1]?._id ||
            null,
          text:
            existingOptions?.[1]?.text ||
            "False",
          isCorrect:
            existingOptions?.[1]?.isCorrect ||
            false,
        },
      ];
    }

    if (
      Array.isArray(existingOptions) &&
      existingOptions.length > 0
    ) {
      return existingOptions;
    }

    return [
      {
        _id: null,
        text: "",
        isCorrect: false,
      },
      {
        _id: null,
        text: "",
        isCorrect: false,
      },
      {
        _id: null,
        text: "",
        isCorrect: false,
      },
      {
        _id: null,
        text: "",
        isCorrect: false,
      },
    ];
  };


  /* =========================================================
     LOAD SCENARIO IN EDIT MODE
     ========================================================= */

  useEffect(() => {
    if (
      !editMode ||
      !scenarioId ||
      !roleId
    ) {
      return;
    }

    if (!accountId) {
      message.error(
        "Admin client ID is missing."
      );
      return;
    }

    const loadScenario = async () => {
      setLoadingEdit(true);

      try {
        const response =
          await getScenarioById(
            Number(roleId),
            scenarioId,
            {
              adminClientId: Number(accountId),
            }
          );

        const scenarioData =
          response?.data || response;

        if (!scenarioData) {
          throw new Error(
            "Scenario data not found."
          );
        }

        const video =
          scenarioData.video || {};

        setFormData({
          scenarioTypeId:
            scenarioData.scenarioTypeId ??
            scenarioTypes.find(
              (item) =>
                String(item.value).toLowerCase() ===
                String(
                  scenarioData.scenarioType || ""
                ).toLowerCase()
            )?.id ??
            "",

          videoCode:
            video.duration ?? "",

          videoTitle:
            getTextValue(video.title),

          videoThumbnailCode:
            video.thumbnailCode || "",

          videoLanguageId:
            video.videoLanguageId ??
            videoLanguages.find(
              (item) =>
                String(item.value).toLowerCase() ===
                String(
                  video.language || ""
                ).toLowerCase()
            )?.id ??
            "",

          videoDescription:
            getTextValue(video.description),

          duration:
            video.duration || "",
        });


        setIsScenarioActive(
          scenarioData.isActive !== false
        );


        const apiQuestions =
          Array.isArray(
            scenarioData.questions
          )
            ? scenarioData.questions.filter(
              (question) =>
                question?.isDeleted !== true
            )
            : [];


        if (apiQuestions.length > 0) {
          setQuizzes(
            apiQuestions.map(
              (question) => {
                const questionTypeId =
                  Number(
                    question.questionTypeId
                  );

                const apiOptions =
                  Array.isArray(
                    question.options
                  )
                    ? question.options
                    : [];

                return {
                  id:
                    question.id ||
                    question._id ||
                    null,

                  quiz:
                    getTextValue(
                      question.title
                    ),

                  description:
                    getTextValue(
                      question.description
                    ),

                  questionTypeId,

                  options:
                    createOptionsForType(
                      questionTypeId,
                      apiOptions.map(
                        (option) => ({
                          _id:
                            option._id ||
                            option.id ||
                            null,

                          text:
                            getTextValue(
                              option.text
                            ),

                          isCorrect:
                            Boolean(
                              option.isCorrect
                            ),
                        })
                      )
                    ),

                  weightage:
                    question.weightage ??
                    1,

                  isActive:
                    question.isActive !==
                    false,
                };
              }
            )
          );
          setExpandedQuizzes({
            0: true,
          });
        } else {
          setQuizzes([
            makeEmptyQuiz(),
          ]);
        }
      } catch (error) {
        console.error(
          "Error fetching scenario:",
          error
        );

        const backendMessage =
          error?.response?.data?.message;

        message.error(
          backendMessage ||
          "Failed to load scenario data."
        );
      } finally {
        setLoadingEdit(false);
      }
    };


    if (
      scenarioTypes.length > 0 &&
      videoLanguages.length > 0
    ) {
      loadScenario();
    }
  }, [
    editMode,
    scenarioId,
    roleId,
    accountId,
    scenarioTypes,
    videoLanguages,
  ]);


  /* =========================================================
     SIMPLE FIELD CHANGE
     ========================================================= */



  const normalizeVideoCode = (input) => {
    if (!input) return "";

    const trimmed = input.trim();

    if (
      trimmed.includes("youtube.com") ||
      trimmed.includes("youtu.be")
    ) {
      try {
        const url = new URL(
          trimmed.includes("://")
            ? trimmed
            : `https://${trimmed}`
        );

        if (url.hostname.includes("youtu.be")) {
          return url.pathname.replace("/", "");
        }

        if (url.searchParams.has("v")) {
          return url.searchParams.get("v") || "";
        }
      } catch {
        // Fall through.
      }
    }

    if (trimmed.includes("&")) {
      return trimmed.split("&")[0];
    }

    return trimmed;
  };

  const handleVideoCodeChange = (value) => {
    const normalized = normalizeVideoCode(value);
    const currentThumbnail =
      formData.videoThumbnailCode;
    const isAutoGenerated =
      currentThumbnail &&
      currentThumbnail.includes(
        "https://img.youtube.com/vi/"
      );
    const shouldSetThumbnail =
      normalized &&
      (isAutoGenerated || !currentThumbnail);
    const newThumbnail = shouldSetThumbnail
      ? `https://img.youtube.com/vi/${normalized}/0.jpg`
      : currentThumbnail;

    setFormData((prev) => ({
      ...prev,
      videoCode: value,
      ...(shouldSetThumbnail
        ? { videoThumbnailCode: newThumbnail }
        : {}),
    }));

    setErrors((prev) => {
      const updated = { ...prev };
      if (updated.videoCode) {
        delete updated.videoCode;
      }
      if (
        shouldSetThumbnail &&
        updated.videoThumbnailCode
      ) {
        delete updated.videoThumbnailCode;
      }
      return updated;
    });
  };

  const handleChange = (
    field,
    value
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const updated = {
          ...prev,
        };

        delete updated[field];

        return updated;
      });
    }
  };


  /* =========================================================
     QUIZ CHANGE
     ========================================================= */

  const updateQuiz = (
    quizIndex,
    field,
    value
  ) => {
    setQuizzes((prev) =>
      prev.map((quiz, index) =>
        index === quizIndex
          ? {
            ...quiz,
            [field]: value,
          }
          : quiz
      )
    );

    setErrors((prev) => ({
      ...prev,
      [`quiz_${quizIndex}_${field}`]:
        undefined,
    }));
  };


  /* =========================================================
     QUESTION TYPE CHANGE
     ========================================================= */

  const handleQuestionTypeChange = (
    quizIndex,
    questionTypeId
  ) => {
    setQuizzes((prev) =>
      prev.map((quiz, index) => {
        if (index !== quizIndex) {
          return quiz;
        }

        return {
          ...quiz,

          questionTypeId,

          options:
            createOptionsForType(
              questionTypeId
            ),
        };
      })
    );

    setErrors((prev) => {
      const updated = {
        ...prev,
      };

      delete updated[
        `quiz_${quizIndex}_questionTypeId`
      ];

      return updated;
    });
  };


  /* =========================================================
     OPTION TEXT CHANGE
     ========================================================= */

  const handleOptionTextChange = (
    quizIndex,
    optionIndex,
    value
  ) => {
    setQuizzes((prev) =>
      prev.map((quiz, index) => {
        if (index !== quizIndex) {
          return quiz;
        }

        const options =
          quiz.options.map(
            (option, index2) =>
              index2 === optionIndex
                ? {
                  ...option,
                  text: value,
                }
                : option
          );

        return {
          ...quiz,
          options,
        };
      })
    );

    setErrors((prev) => ({
      ...prev,
      [`quiz_${quizIndex}_option_${optionIndex}`]:
        undefined,
    }));
  };


  /* =========================================================
     CORRECT ANSWER
     ========================================================= */

  const handleCorrectAnswerChange = (
    quizIndex,
    optionIndex,
    checked
  ) => {
    setQuizzes((prev) =>
      prev.map((quiz, index) => {
        if (index !== quizIndex) {
          return quiz;
        }

        const multi =
          isMultiSelectType(
            quiz.questionTypeId
          );

        const options =
          quiz.options.map(
            (option, index2) => {
              if (multi) {
                return index2 ===
                  optionIndex
                  ? {
                    ...option,
                    isCorrect:
                      checked,
                  }
                  : option;
              }

              return {
                ...option,
                isCorrect:
                  index2 ===
                  optionIndex,
              };
            }
          );

        return {
          ...quiz,
          options,
        };
      })
    );

    setErrors((prev) => ({
      ...prev,
      [`quiz_${quizIndex}_correctAnswer`]:
        undefined,
    }));
  };


  /* =========================================================
     ADD OPTION
     ========================================================= */

  const addOption = (
    quizIndex
  ) => {
    setQuizzes((prev) =>
      prev.map((quiz, index) => {
        if (index !== quizIndex) {
          return quiz;
        }

        if (
          isYesNoType(
            quiz.questionTypeId
          )
        ) {
          return quiz;
        }

        return {
          ...quiz,

          options: [
            ...quiz.options,
            {
              _id: null,
              text: "",
              isCorrect: false,
            },
          ],
        };
      })
    );
  };


  /* =========================================================
     REMOVE OPTION
     ========================================================= */

  const removeOption = (
    quizIndex,
    optionIndex
  ) => {
    setQuizzes((prev) =>
      prev.map((quiz, index) => {
        if (index !== quizIndex) {
          return quiz;
        }

        if (
          quiz.options.length <= 2 ||
          isYesNoType(
            quiz.questionTypeId
          )
        ) {
          return quiz;
        }

        return {
          ...quiz,

          options:
            quiz.options.filter(
              (_, index2) =>
                index2 !== optionIndex
            ),
        };
      })
    );
  };


  /* =========================================================
     ADD QUIZ
     ========================================================= */

  const addQuiz = () => {
    const newIndex = quizzes.length;

    setQuizzes((prev) => [
      ...prev,
      makeEmptyQuiz(),
    ]);

    setExpandedQuizzes((expanded) => ({
      ...expanded,
      [newIndex]: true,
    }));
  };

  const toggleQuiz = (quizIndex) => {
    setExpandedQuizzes((prev) => ({
      ...prev,
      [quizIndex]: !prev[quizIndex],
    }));
  };


  /* =========================================================
     DELETE QUIZ
     ========================================================= */

  const deleteQuiz = async (
    quizIndex
  ) => {
    const quiz =
      quizzes[quizIndex];

    /*
     * New unsaved quiz:
     * simply remove it locally.
     */
    if (!quiz?.id) {
      setQuizzes((prev) =>
        prev.filter(
          (_, index) =>
            index !== quizIndex
        )
      );

      setExpandedQuizzes((prev) => {
        const updated = {};

        Object.keys(prev).forEach((key) => {
          const index = Number(key);

          if (index < quizIndex) {
            updated[index] = prev[key];
          } else if (index > quizIndex) {
            updated[index - 1] = prev[key];
          }
        });

        return updated;
      });

      return;
    }

    if (!accountId) {
      message.error(
        "Admin client ID is missing."
      );
      return;
    }

    if (!scenarioId) {
      return;
    }

    try {
      await deleteScenarioQuestion(
        Number(roleId),
        scenarioId,
        quiz.id,
        {
          adminClientId:
            Number(accountId),

          changedBy:
            changedBy !== undefined &&
              changedBy !== null
              ? Number(changedBy)
              : undefined,
        }
      );

      setQuizzes((prev) =>
        prev.filter(
          (_, index) =>
            index !== quizIndex
        )
      );

      setExpandedQuizzes((prev) => {
        const updated = {};

        Object.keys(prev).forEach((key) => {
          const index = Number(key);

          if (index < quizIndex) {
            updated[index] = prev[key];
          } else if (index > quizIndex) {
            updated[index - 1] = prev[key];
          }
        });

        return updated;
      });

      message.success(
        "Question deleted successfully."
      );
    } catch (error) {
      console.error(
        "Error deleting scenario question:",
        error
      );

      const backendMessage =
        error?.response?.data?.message;

      message.error(
        backendMessage ||
        "Failed to delete question."
      );
    }
  };


  /* =========================================================
     VALIDATION
     ========================================================= */

  const validate = () => {
    const newErrors = {};


    if (
      !formData.scenarioTypeId
    ) {
      newErrors.scenarioTypeId =
        "Please select a scenario type.";
    }


    if (
      !formData.videoCode?.trim()
    ) {
      newErrors.videoCode =
        "Video code is required.";
    }


    if (
      !formData.videoTitle?.trim()
    ) {
      newErrors.videoTitle =
        "Video title is required.";
    }


    if (
      !formData.videoThumbnailCode?.trim()
    ) {
      newErrors.videoThumbnailCode =
        "Thumbnail code is required.";
    }


    if (
      !formData.videoLanguageId
    ) {
      newErrors.videoLanguageId =
        "Please select a language.";
    }


    if (
      !formData.videoDescription?.trim()
    ) {
      newErrors.videoDescription =
        "Video description is required.";
    }


    quizzes.forEach(
      (quiz, quizIndex) => {
        if (!quiz.quiz?.trim()) {
          newErrors[
            `quiz_${quizIndex}_quiz`
          ] =
            "Quiz question is required.";
        }

        if (
          !quiz.questionTypeId
        ) {
          newErrors[
            `quiz_${quizIndex}_questionTypeId`
          ] =
            "Question type is required.";
        }

        if (
          quiz.weightage === "" ||
          quiz.weightage === null ||
          Number(quiz.weightage) <= 0
        ) {
          newErrors[
            `quiz_${quizIndex}_weightage`
          ] =
            "Weightage must be greater than zero.";
        }


        if (
          !Array.isArray(
            quiz.options
          ) ||
          quiz.options.length < 2
        ) {
          newErrors[
            `quiz_${quizIndex}_options`
          ] =
            "Minimum two options are required.";
        }


        const correctCount =
          quiz.options.filter(
            (option) =>
              option.isCorrect
          ).length;


        if (
          isMultiSelectType(
            quiz.questionTypeId
          )
        ) {
          if (correctCount < 1) {
            newErrors[
              `quiz_${quizIndex}_correctAnswer`
            ] =
              "Select at least one correct answer.";
          }
        } else {
          if (correctCount !== 1) {
            newErrors[
              `quiz_${quizIndex}_correctAnswer`
            ] =
              "Select exactly one correct answer.";
          }
        }


        quiz.options.forEach(
          (option, optionIndex) => {
            if (
              !option.text?.trim()
            ) {
              newErrors[
                `quiz_${quizIndex}_option_${optionIndex}`
              ] =
                "This option is required.";
            }
          }
        );
      }
    );


    setErrors(newErrors);

    return (
      Object.keys(newErrors)
        .length === 0
    );
  };


  /* =========================================================
     BACKEND ERROR HANDLER
     ========================================================= */

  const applyBackendErrors = (
    error
  ) => {
    const backendErrors =
      error?.response?.data?.errors;

    if (
      !Array.isArray(
        backendErrors
      )
    ) {
      return;
    }

    const mappedErrors = {};

    backendErrors.forEach(
      (item) => {
        if (
          item?.field
        ) {
          mappedErrors[
            item.field
          ] =
            item.message;
        }
      }
    );

    setErrors((prev) => ({
      ...prev,
      ...mappedErrors,
    }));
  };


  /* =========================================================
     SAVE
     ========================================================= */

  const handleSave = async () => {
    if (!validate()) {
      message.warning(
        "Please fill all required fields."
      );
      return;
    }


    if (!roleId) {
      message.error(
        "Role ID is missing."
      );
      return;
    }


    if (!accountId) {
      message.error(
        "Admin client ID is missing."
      );
      return;
    }


    setSaving(true);


    try {
      /* =====================================================
         SCENARIO PAYLOAD
         ===================================================== */

      const scenarioPayload = {
        scenarioTypeId: Number(
          formData.scenarioTypeId
        ),

        video: {
          title: formData.videoTitle.trim(),
          videoCode: formData.videoCode.trim(),
          thumbnailCode:
            formData.videoThumbnailCode.trim(),
          duration:
            formData.duration?.trim() || "",
          description:
            formData.videoDescription.trim(),
          videoLanguageId: Number(
            formData.videoLanguageId
          ),
        },
      };


      /*
       * Active status belongs ONLY
       * to edit/update.
       *
       * POST does not expose
       * the active toggle.
       */
      if (editMode) {
        scenarioPayload.isActive =
          isScenarioActive;
      }


      /* =====================================================
         CREATE SCENARIO
         ===================================================== */

      if (!editMode) {
        const response =
          await createScenario(
            Number(roleId),
            scenarioPayload,
            {
              adminClientId:
                Number(accountId),

              changedBy:
                changedBy !== undefined &&
                  changedBy !== null
                  ? Number(changedBy)
                  : undefined,
            }
          );


        const createdScenario =
          response?.data ||
          response;


        const createdScenarioId =
          createdScenario?.id ||
          createdScenario?._id;


        if (!createdScenarioId) {
          throw new Error(
            "Scenario was created but its ID was not returned."
          );
        }


        /* ===================================================
           CREATE EACH QUESTION SEPARATELY
           =================================================== */

        for (
          const quiz of quizzes
        ) {
          const questionPayload = {
            title: quiz.quiz.trim(),

            description:
              quiz.description?.trim() || "",

            questionTypeId: Number(
              quiz.questionTypeId
            ),

            options: quiz.options.map(
              (option) => ({
                ...(option._id
                  ? { _id: option._id }
                  : {}),

                text: option.text.trim(),

                isCorrect: Boolean(
                  option.isCorrect
                ),
              })
            ),

            weightage: Number(
              quiz.weightage
            ),
          };


          await createScenarioQuestion(
            Number(roleId),
            createdScenarioId,
            questionPayload,
            {
              adminClientId:
                Number(accountId),

              changedBy:
                changedBy !== undefined &&
                  changedBy !== null
                  ? Number(changedBy)
                  : undefined,
            }
          );
        }


        message.success(
          "Scenario saved successfully."
        );

        navigate(-1);

        return;
      }


      /* =====================================================
         EDIT SCENARIO
         ===================================================== */

      await updateScenario(
        Number(roleId),
        scenarioId,
        scenarioPayload,
        {
          adminClientId:
            Number(accountId),

          changedBy:
            changedBy !== undefined &&
              changedBy !== null
              ? Number(changedBy)
              : undefined,
        }
      );


      /* =====================================================
         UPDATE / CREATE QUESTIONS
         ===================================================== */

      for (
        const quiz of quizzes
      ) {
        const questionPayload = {
          title:
            quiz.quiz.trim(),

          ...(quiz.description?.trim()
            ? {
              description:
                quiz.description.trim(),
            }
            : {}),

          questionTypeId:
            Number(
              quiz.questionTypeId
            ),

          options:
            quiz.options.map(
              (option) => {
                const optionPayload = {
                  text:
                    option.text.trim(),

                  isCorrect:
                    Boolean(
                      option.isCorrect
                    ),
                };

                /*
                 * IMPORTANT:
                 * Existing option IDs must
                 * be preserved for PATCH.
                 */
                if (
                  option._id
                ) {
                  optionPayload._id =
                    option._id;
                }

                return optionPayload;
              }
            ),

          weightage:
            Number(
              quiz.weightage
            ),

          isActive:
            quiz.isActive !== false,
        };


        if (quiz.id) {
          await updateScenarioQuestion(
            Number(roleId),
            scenarioId,
            quiz.id,
            questionPayload,
            {
              adminClientId:
                Number(accountId),

              changedBy:
                changedBy !== undefined &&
                  changedBy !== null
                  ? Number(changedBy)
                  : undefined,
            }
          );
        } else {
          await createScenarioQuestion(
            Number(roleId),
            scenarioId,
            questionPayload,
            {
              adminClientId:
                Number(accountId),

              changedBy:
                changedBy !== undefined &&
                  changedBy !== null
                  ? Number(changedBy)
                  : undefined,
            }
          );
        }
      }


      message.success(
        "Scenario updated successfully."
      );

      navigate(-1);

    } catch (error) {
      console.error(
        "Error saving scenario:",
        error
      );

      applyBackendErrors(
        error
      );

      const backendMessage =
        error?.response?.data?.message;

      if (
        backendMessage
      ) {
        message.error(
          backendMessage
        );
      } else if (
        error?.message
      ) {
        message.error(
          error.message
        );
      } else {
        message.error(
          editMode
            ? "Failed to update scenario."
            : "Failed to save scenario."
        );
      }

    } finally {
      setSaving(false);
    }
  };


  const inputErrorClass = (
    field
  ) =>
    errors[field]
      ? "border-red-500"
      : "border-gray-300";


  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="scenario-form bg-[#F8F8F8] min-h-screen px-8 py-3 -mt-[25px]">

      {/* Back Button */}

      <button
        onClick={() =>
          navigate(-1)
        }
        className="flex items-center gap-2 text-[#C40000] text-sm font-medium mb-2 -mt-0"
      >
        <ArrowLeft size={16} />
        Back
      </button>


      {/* Heading */}

      <div className="flex justify-between items-center mb-1 mt-2">

        <h2 className="text-xl font-semibold">
          {editMode
            ? "Edit Scenario-Based Assessment"
            : "Add Scenario-Based Assessment"}
        </h2>


        {/* ACTIVE TOGGLE ONLY IN EDIT */}

        {editMode && (
          <div className="flex items-center gap-2">

            <Switch
              checked={
                isScenarioActive
              }
              onChange={
                setIsScenarioActive
              }
              checkedChildren=""
              unCheckedChildren=""
            />

            <span className="text-sm">
              {isScenarioActive
                ? "Active"
                : "Inactive"}
            </span>

          </div>
        )}

      </div>


      {loadingEdit ? (
        <div className="flex justify-center items-center h-[200px]">
          <Spin size="large" />
        </div>
      ) : (
        <>

          {/* ===================================================
              SCENARIO TYPE
              =================================================== */}

          <div className="mb-8">

            <label className="block text-sm font-medium mb-1">
              Select Scenario Type
            </label>

            <Select
              className={`w-72 ${errors.scenarioTypeId
                ? "border-red-500"
                : ""
                }`}
              status={errors.scenarioTypeId ? "error" : ""}
              placeholder="select the option"
              value={
                formData.scenarioTypeId ||
                undefined
              }
              loading={
                loadingOptions
              }
              // disabled={
              //   editMode
              // }
              onChange={(value) =>
                handleChange(
                  "scenarioTypeId",
                  Number(value)
                )
              }
              options={scenarioTypes.map(
                (item) => ({
                  value: Number(
                    item.id
                  ),
                  label:
                    item.name ||
                    item.value,
                })
              )}
            />

            {errors.scenarioTypeId && (
              <p className="text-red-500 text-xs mt-1">
                {errors.scenarioTypeId}
              </p>
            )}

          </div>


          {/* ===================================================
              SCENARIO DETAILS
              =================================================== */}

          <div className="bg-white rounded-lg border p-6 mb-3">

            <h3 className="font-semibold text-base mb-3">
              Scenario Details
            </h3>


            <div className="grid grid-cols-2 gap-4">

              {/* VIDEO CODE */}
              <div>
                <label className="text-sm">
                  Video Code{" "}
                  <span className="text-red-500">*</span>
                </label>

                <input
                  className={`w-full border rounded-md h-8 px-3 mt-2 ${inputErrorClass(
                    "videoCode"
                  )}`}
                  placeholder="Enter video code"
                  value={formData.videoCode}
                  onChange={(e) =>
                    handleVideoCodeChange(e.target.value)
                  }
                />

                {errors.videoCode && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.videoCode}
                  </p>
                )}
              </div>

              {/* VIDEO TITLE */}
              <div>
                <label className="text-sm">
                  Video Title{" "}
                  <span className="text-red-500">*</span>
                </label>

                <input
                  className={`w-full border rounded-md h-8 px-3 mt-1 ${inputErrorClass(
                    "videoTitle"
                  )}`}
                  placeholder="Enter video title"
                  value={formData.videoTitle}
                  onChange={(e) =>
                    handleChange(
                      "videoTitle",
                      e.target.value
                    )
                  }
                />

                {errors.videoTitle && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.videoTitle}
                  </p>
                )}
              </div>

              {/* VIDEO THUMBNAIL CODE */}
              <div>
                <label className="text-sm">
                  Video Thumbnail Code{" "}
                  <span className="text-red-500">*</span>
                </label>

                <input
                  className={`w-full border rounded-md h-8 px-3 mt-1 ${inputErrorClass(
                    "videoThumbnailCode"
                  )}`}
                  placeholder="Enter video thumbnail code"
                  disabled={true}
                  value={formData.videoThumbnailCode}
                  onChange={(e) =>
                    handleChange(
                      "videoThumbnailCode",
                      e.target.value
                    )
                  }
                />

                {errors.videoThumbnailCode && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.videoThumbnailCode}
                  </p>
                )}
              </div>

              {/* VIDEO LANGUAGE */}
              <div>
                <label className="text-sm">
                  Video Language{" "}
                  <span className="text-red-500">*</span>
                </label>

                <Select
                  className="w-full mt-1"
                  status={
                    errors.videoLanguageId ? "error" : ""
                  }
                  placeholder="Select Language"
                  loading={loadingOptions}
                  value={
                    formData.videoLanguageId || undefined
                  }
                  onChange={(value) =>
                    handleChange(
                      "videoLanguageId",
                      Number(value)
                    )
                  }
                  options={videoLanguages.map((item) => ({
                    value: Number(item.id),
                    label: item.name || item.value,
                  }))}
                />

                {errors.videoLanguageId && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.videoLanguageId}
                  </p>
                )}
              </div>

              {/* VIDEO DURATION */}

              <div>
                <label className="text-sm">
                  Video Duration{" "}
                  <span className="text-red-500">*</span>
                </label>

                <input
                  type="text"
                  placeholder="MM:SS"
                  value={formData.duration}
                  maxLength={5}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, "");

                    // Allow at most 4 digits: MMSS
                    value = value.slice(0, 4);

                    // Automatically insert colon after MM
                    if (value.length > 2) {
                      value =
                        value.slice(0, 2) +
                        ":" +
                        value.slice(2);
                    }

                    handleChange("duration", value);
                  }}
                  onBlur={() => {
                    const value = formData.duration;

                    if (
                      value &&
                      !/^\d{2}:[0-5]\d$/.test(value)
                    ) {
                      setErrors((prev) => ({
                        ...prev,
                        duration: "Enter duration in MM:SS format (e.g., 05:30).",
                      }));
                    }
                  }}
                  className={`w-full border rounded-md h-8 px-3 mt-1 ${inputErrorClass(
                    "duration"
                  )}`}
                />

                {errors.duration && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.duration}
                  </p>
                )}
              </div>


            </div>

            {/* DESCRIPTION */}

            <div className="mt-3">

              <label className="text-sm">
                Video Description{" "}
                <span className="text-red-500">
                  *
                </span>
              </label>

              <textarea
                rows="3"
                className={`w-full border rounded-md p-2 mt-1 ${inputErrorClass(
                  "videoDescription"
                )}`}
                placeholder="Enter video description"
                value={
                  formData.videoDescription
                }
                onChange={(e) =>
                  handleChange(
                    "videoDescription",
                    e.target.value
                  )
                }
              />

              {errors.videoDescription && (
                <p className="text-red-500 text-xs mt-1">
                  {
                    errors.videoDescription
                  }
                </p>
              )}

            </div>

          </div>


          {/* ===================================================
              QUIZ QUESTIONS
              =================================================== */}

          {quizzes.map(
            (
              quiz,
              quizIndex
            ) => (

              <React.Fragment
                key={
                  quiz.id ||
                  `new-${quizIndex}`
                }
              >
                <div className="bg-white rounded-lg border p-6 mb-3">

                  {/* QUIZ HEADER */}

                  <div
                    className="
                    flex
                    justify-between
                    items-center
                    border
                    border-gray-200
                    rounded-lg
                    px-4
                    py-2
                    cursor-pointer
                  "
                    onClick={() =>
                      toggleQuiz(quizIndex)
                    }
                  >

                    <h3 className="font-semibold text-base">
                      Quiz {quizIndex + 1}
                    </h3>

                    <div
                      className="
                      flex
                      items-center
                      gap-3
                    "
                      onClick={(e) =>
                        e.stopPropagation()
                      }
                    >

                      <button
                        type="button"
                        onClick={() =>
                          deleteQuiz(quizIndex)
                        }
                        className="
                        border
                        border-[#C40000]
                        text-[#C40000]
                        px-3
                        py-1.5
                        rounded-md
                        text-sm
                        font-medium
                        hover:bg-red-50
                      "
                      >
                        Delete
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          toggleQuiz(quizIndex)
                        }
                        className="
                        w-8
                        h-8
                        rounded-full
                        bg-[#FBEAEA]
                        flex
                        items-center
                        justify-center
                        text-[#C40000]
                      "
                      >
                        {expandedQuizzes[quizIndex] ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )}
                      </button>

                    </div>

                  </div>

                  {expandedQuizzes[quizIndex] && (
                    <>
                      {/*
              * =================================================
              * QUESTION + TYPE
              * =================================================
              */}

                      <div
                        className="
                  grid
                  grid-cols-2
                  gap-x-4
                  gap-y-3
                  mt-4
                "
                      >

                        {/* QUESTION */}

                        <div>

                          <label
                            className="
                      block
                      mb-1
                      text-[14px]
                      font-medium
                    "
                          >
                            Quiz{" "}
                            <span className="text-red-600">
                              *
                            </span>
                          </label>

                          <input
                            value={
                              quiz.quiz
                            }
                            onChange={(e) =>
                              updateQuiz(
                                quizIndex,
                                "quiz",
                                e.target.value
                              )
                            }
                            placeholder="Enter Quiz Question"
                            className={`
                      w-full
                      h-10
                      rounded-md
                      px-3
                      text-[14px]
                      border
                      outline-none

                      ${errors[
                                `quiz_${quizIndex}_quiz`
                              ]
                                ? "border-red-500"
                                : "border-gray-300"
                              }
                    `}
                          />

                          {errors[
                            `quiz_${quizIndex}_quiz`
                          ] && (
                              <p className="
                      text-red-500
                      text-sm
                      mt-1
                    ">
                                {
                                  errors[
                                  `quiz_${quizIndex}_quiz`
                                  ]
                                }
                              </p>
                            )}

                        </div>


                        {/* QUESTION TYPE */}

                        <div>

                          <label
                            className="
                      block
                      mb-1
                      text-[14px]
                      font-medium
                    "
                          >
                            Question Type

                            <span className="text-red-600">
                              *
                            </span>
                          </label>

                          <Select
                            showSearch
                            className="
                      w-full
                      custom-select
                    "
                            placeholder="Select Question Type"
                            loading={
                              loadingOptions
                            }
                            // disabled={
                            //   editMode
                            // }
                            value={
                              quiz.questionTypeId ||
                              undefined
                            }
                            optionFilterProp="label"
                            filterOption={(
                              input,
                              option
                            ) =>
                              (
                                option?.label ||
                                ""
                              )
                                .toLowerCase()
                                .includes(
                                  input.toLowerCase()
                                )
                            }
                            options={
                              questionTypes.map(
                                (
                                  type
                                ) => ({
                                  value:
                                    Number(
                                      type.id
                                    ),
                                  label:
                                    type.name ||
                                    type.value,
                                })
                              )
                            }
                            onChange={(value) =>
                              handleQuestionTypeChange(
                                quizIndex,
                                Number(value)
                              )
                            }
                            status={
                              errors[
                                `quiz_${quizIndex}_questionTypeId`
                              ]
                                ? "error"
                                : ""
                            }
                          />

                          {errors[
                            `quiz_${quizIndex}_questionTypeId`
                          ] && (
                              <p className="
                      text-red-500
                      text-sm
                      mt-1
                    ">
                                {
                                  errors[
                                  `quiz_${quizIndex}_questionTypeId`
                                  ]
                                }
                              </p>
                            )}

                        </div>

                      </div>


                      <hr className="my-4" />


                      {/* OPTIONS HEADER */}

                      <div
                        className="
    flex
    justify-between
    items-center
  "
                      >
                        <h3
                          className="
      text-lg
      font-bold
    "
                        >
                          Options
                        </h3>

                        {!isYesNoType(quiz.questionTypeId) && (
                          <button
                            type="button"
                            onClick={() => addOption(quizIndex)}
                            className="
        text-[#D50000]
        font-semibold
      "
                          >
                            Add Option
                          </button>
                        )}
                      </div>

                      {/* OPTIONS */}

                      <div
                        className="
    grid
    grid-cols-2
      gap-x-4
    gap-y-3
    mt-3
  "
                      >
                        {quiz.options.map((option, optionIndex) => (
                          <div
                            key={
                              option._id ||
                              `${quizIndex}-${optionIndex}`
                            }
                          >
                            <label
                              className="
          block
          font-medium
          mb-1
          text-sm
        "
                            >
                              Option {optionIndex + 1}

                              <span className="text-red-600">
                                *
                              </span>
                            </label>

                            {/* INPUT + DELETE ICON */}
                            <div className="relative">
                              <input
                                type="text"
                                value={option.text}
                                disabled={isYesNoType(quiz.questionTypeId)}
                                placeholder={
                                  isYesNoType(quiz.questionTypeId)
                                    ? ""
                                    : `Enter Option ${optionIndex + 1}`
                                }
                                onChange={(e) =>
                                  handleOptionTextChange(
                                    quizIndex,
                                    optionIndex,
                                    e.target.value
                                  )
                                }
                                className={`
            w-full
            h-10
            rounded-md
            px-3
            text-[14px]
            border
            outline-none

            ${!isYesNoType(quiz.questionTypeId) &&
                                    quiz.options.length > 4
                                    ? "pr-10"
                                    : ""
                                  }

            ${isYesNoType(quiz.questionTypeId)
                                    ? "bg-gray-100 cursor-not-allowed"
                                    : ""
                                  }

            ${errors[
                                    `quiz_${quizIndex}_option_${optionIndex}`
                                  ]
                                    ? "border-red-500"
                                    : "border-gray-300"
                                  }
          `}
                              />

                              {/* DELETE ICON */}
                              {!isYesNoType(quiz.questionTypeId) &&
                                quiz.options.length > 4 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      removeOption(
                                        quizIndex,
                                        optionIndex
                                      )
                                    }
                                    className="
                absolute
                right-2
                top-1/2
                -translate-y-1/2
                flex
                items-center
                justify-center
                bg-transparent
                border-none
                cursor-pointer
                p-0
                z-10
              "
                                  >
                                    <img
                                      src={DeleteIcon}
                                      alt="Delete"
                                      className="
                  w-[16px]
                  h-[16px]
                  object-contain
                "
                                    />
                                  </button>
                                )}
                            </div>

                            {/* OPTION ERROR */}
                            {errors[
                              `quiz_${quizIndex}_option_${optionIndex}`
                            ] && (
                                <p
                                  className="
            text-red-500
            text-sm
            mt-1
          "
                                >
                                  {
                                    errors[
                                    `quiz_${quizIndex}_option_${optionIndex}`
                                    ]
                                  }
                                </p>
                              )}
                          </div>
                        ))}
                      </div>

                      {/* OPTIONS ERROR */}

                      {errors[
                        `quiz_${quizIndex}_options`
                      ] && (
                          <p
                            className="
      text-red-500
      text-sm
      mt-2
    "
                          >
                            {
                              errors[
                              `quiz_${quizIndex}_options`
                              ]
                            }
                          </p>
                        )}


                      {/* CORRECT ANSWER */}

                      <div className="
                mt-4
              ">

                        <h3 className="
                  text-base
                  font-semibold
                  mb-2
                ">
                          Correct Answer

                          <span className="
                    text-red-600
                  ">
                            *
                          </span>
                        </h3>


                        <div className="
                  flex
                  flex-wrap
                  items-center
                  gap-5
                ">

                          {quiz.options.map(
                            (
                              option,
                              optionIndex
                            ) => (

                              <label
                                key={
                                  option._id ||
                                  `${quizIndex}-correct-${optionIndex}`
                                }
                                className="
                          flex
                          items-center
                          gap-2
                          cursor-pointer
                        "
                              >

                                {isMultiSelectType(
                                  quiz.questionTypeId
                                ) ? (

                                  <input
                                    type="checkbox"
                                    checked={
                                      option.isCorrect
                                    }
                                    onChange={(e) =>
                                      handleCorrectAnswerChange(
                                        quizIndex,
                                        optionIndex,
                                        e.target.checked
                                      )
                                    }
                                    className="
                              w-4
                              h-4
                              accent-[#D50000]
                            "
                                  />

                                ) : (

                                  <input
                                    type="radio"
                                    name={`correctAnswer-${quiz.id || quizIndex}`}
                                    checked={
                                      option.isCorrect
                                    }
                                    onChange={() =>
                                      handleCorrectAnswerChange(
                                        quizIndex,
                                        optionIndex,
                                        true
                                      )
                                    }
                                    className="
                              w-4
                              h-4
                              accent-[#D50000]
                            "
                                  />

                                )}

                                <span className="
                          text-[14px]
                          text-[#3A3A3A]
                        ">
                                  {isYesNoType(
                                    quiz.questionTypeId
                                  )
                                    ? option.text
                                    : `Option ${optionIndex +
                                    1
                                    }`}
                                </span>

                              </label>

                            )
                          )}

                        </div>


                        {errors[
                          `quiz_${quizIndex}_correctAnswer`
                        ] && (
                            <p className="
                    text-red-500
                    text-sm
                    mt-3
                  ">
                              {
                                errors[
                                `quiz_${quizIndex}_correctAnswer`
                                ]
                              }
                            </p>
                          )}

                      </div>


                    </>
                  )}
                </div>
                {expandedQuizzes[quizIndex] && (
                  <div className="scenario-weightage bg-white rounded-lg border p-6 mb-3">
                    <h3 className="text-lg font-semibold mb-4">
                      Weightage (Points)
                    </h3>

                    <div className="w-full max-w-[320px]">
                      <label className="block text-sm mb-1">
                        Weightage{" "}
                        <span className="text-red-500">*</span>
                      </label>

                      <div className="weightage-number-input">
                        <input
                          type="number"
                          min="1"
                          placeholder="Enter Weightage"
                          value={quiz.weightage ?? ""}
                          onWheel={(e) => {
                            e.preventDefault();
                            e.currentTarget.blur();
                          }}
                          onChange={(e) => {
                            updateQuiz(
                              quizIndex,
                              "weightage",
                              e.target.value
                            );
                          }}
                          className={`w-full border rounded-md px-3 pr-10 outline-none text-sm ${errors[`quiz_${quizIndex}_weightage`]
                            ? "border-red-500"
                            : "border-gray-300"
                            }`}
                        />
                        <div className="weightage-number-stepper">
                          <button
                            type="button"
                            aria-label="Increase weightage"
                            onClick={() =>
                              updateQuiz(
                                quizIndex,
                                "weightage",
                                String(Math.max(1, Number(quiz.weightage) || 0) + 1)
                              )
                            }
                          >
                            <span className="weightage-stepper-arrow weightage-stepper-arrow-up" />
                          </button>
                          <button
                            type="button"
                            aria-label="Decrease weightage"
                            onClick={() =>
                              updateQuiz(
                                quizIndex,
                                "weightage",
                                String(Math.max(1, (Number(quiz.weightage) || 1) - 1))
                              )
                            }
                          >
                            <span className="weightage-stepper-arrow weightage-stepper-arrow-down" />
                          </button>
                        </div>
                      </div>

                      {errors[`quiz_${quizIndex}_weightage`] && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors[`quiz_${quizIndex}_weightage`]}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}

          {/* ADD QUIZ */}

          {/* <div className="
            flex
            justify-start
            mb-5
          ">

            <button
              type="button"
              onClick={addQuiz}
              className="
                border
                border-gray-300
                bg-white
                text-[#C40000]
                px-3
                py-2
                rounded-md
                text-sm
                font-medium
                hover:bg-gray-50
              "
            >
              Add Quiz
            </button>

          </div> */}


          {/* FOOTER */}

          <div className="
            flex
            justify-end
            gap-3
          ">

            <button
              onClick={() =>
                navigate(-1)
              }
              className="
                border
                border-gray-300
                px-4
                h-9
                rounded-md
              "
            >
              Cancel
            </button>

            <button
              onClick={handleSave}
              disabled={
                saving ||
                loadingOptions
              }
              className="
                bg-[#D50000]
                text-white
                px-4
                h-9
                rounded-md
                disabled:opacity-50
              "
            >
              {saving
                ? "Saving..."
                : editMode
                  ? "Update"
                  : "Save"}
            </button>

          </div>

        </>
      )}

    </div>
  );
};


export default AddScenario;